import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../contexts/AuthContext";
import { useCompany } from "../../../contexts/CompanyContext";

interface Member {
  user_id: string;
  job_role: string;
  display_name: string;
  email: string;
  role: string; // admin | student
  joined_at: string;
}

const ROLE_ICONS: Record<string, string> = {
  CEO: "ri-user-star-line",
  AR: "ri-music-2-line",
  Finance: "ri-bank-line",
  Marketing: "ri-megaphone-line",
  Legal: "ri-file-shield-2-line",
  PD: "ri-mic-line",
  Ticketing: "ri-ticket-line",
  Projects: "ri-folder-line",
  Fundraising: "ri-heart-line",
  Finance_npo: "ri-calculator-line",
  Evaluation: "ri-award-line",
  Editor: "ri-edit-line",
  Contract: "ri-file-text-line",
  Creative: "ri-palette-line",
  AE: "ri-briefcase-line",
  Production: "ri-video-line",
  Data: "ri-bar-chart-line",
};

const ROLE_LABEL: Record<string, string> = {
  CEO: "대표이사",
  AR: "A&R 담당",
  Finance: "재무 담당",
  Marketing: "마케팅 담당",
  Legal: "법무 담당",
  PD: "공연기획 PD",
  Ticketing: "티켓영업 MD",
  Projects: "사업팀장",
  Fundraising: "후원개발",
  Finance_npo: "회계담당",
  Evaluation: "사업평가",
  Editor: "에디터",
  Contract: "계약 MD",
  Creative: "크리에이티브",
  AE: "영업 AE",
  Production: "영상 PD",
  Data: "데이터 분석",
};

const ROLE_COLORS = [
  "bg-[#4B6CF7]/15 text-[#7A9BFF]",
  "bg-emerald-400/15 text-emerald-400",
  "bg-amber-400/15 text-amber-400",
  "bg-pink-400/15 text-pink-400",
  "bg-teal-400/15 text-teal-400",
  "bg-orange-400/15 text-orange-400",
];

function avatarColor(str: string): string {
  const idx = str.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0) % ROLE_COLORS.length;
  return ROLE_COLORS[idx];
}

export default function TeamPanel() {
  const { user } = useAuth();
  const { workspaceId } = useCompany();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);

  const fetchMembers = useCallback(async () => {
    if (!workspaceId) { setLoading(false); return; }
    setLoading(true);

    // workspace_members join profiles
    const { data } = await supabase
      .from("workspace_members")
      .select("user_id, job_role, joined_at, profiles:user_id(display_name, email, role)")
      .eq("workspace_id", workspaceId);

    if (data) {
      const parsed: Member[] = data.map((row: {
        user_id: string;
        job_role: string;
        joined_at: string;
        profiles: { display_name: string; email: string; role: string } | null;
      }) => ({
        user_id: row.user_id,
        job_role: row.job_role ?? "?",
        display_name: (row.profiles as { display_name: string } | null)?.display_name ?? "Unknown",
        email: (row.profiles as { email: string } | null)?.email ?? "",
        role: (row.profiles as { role: string } | null)?.role ?? "student",
        joined_at: row.joined_at,
      }));
      setMembers(parsed);
    }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const myId = user?.id;
  const noRole = members.filter((m) => !m.job_role || m.job_role === "?");
  const withRole = members.filter((m) => m.job_role && m.job_role !== "?");

  return (
    <div className="bg-white border border-black/[0.08] rounded-xl flex flex-col overflow-hidden" style={{ minHeight: 288 }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-black/[0.07]">
        <div className="flex items-center gap-2">
          <h3 className="text-[#888888] text-xs font-medium uppercase tracking-widest">팀원 현황</h3>
          {members.length > 0 && (
            <span className="text-[9px] bg-[#4B6CF7]/10 text-[#4B6CF7] border border-[#4B6CF7]/20 px-1.5 py-0.5 rounded-full font-semibold">
              {members.length}명
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {noRole.length > 0 && (
            <span className="text-[9px] text-amber-500/80 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded-full">
              미배정 {noRole.length}명
            </span>
          )}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-[#CCCCCC] hover:text-[#888888] cursor-pointer transition-colors"
          >
            <i className={`ri-arrow-${open ? "up" : "down"}-s-line text-sm`}></i>
          </button>
        </div>
      </div>

      {/* Body */}
      {open && (
        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-4 h-4 border-2 border-black/10 border-t-[#4B6CF7]/40 rounded-full animate-spin"></div>
            </div>
          ) : members.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <i className="ri-group-line text-2xl text-[#DDDDDD] mb-2"></i>
              <p className="text-[#CCCCCC] text-xs">아직 참여한 팀원이 없어요</p>
              <p className="text-[#DDDDDD] text-[10px] mt-1">관리자가 초대 코드를 공유해주세요</p>
            </div>
          ) : (
            <div className="space-y-1">
              {withRole.map((member) => {
                const isMe = member.user_id === myId;
                const initials = member.display_name.slice(0, 2);
                const colorClass = avatarColor(member.user_id);
                const roleLabel = ROLE_LABEL[member.job_role] ?? member.job_role;
                const roleIcon = ROLE_ICONS[member.job_role] ?? "ri-user-line";
                return (
                  <div
                    key={member.user_id}
                    className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-colors ${isMe ? "bg-[#4B6CF7]/[0.04] border border-[#4B6CF7]/10" : "hover:bg-black/[0.02]"}`}
                  >
                    {/* Avatar */}
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full text-[10px] font-bold shrink-0 ${colorClass}`}>
                      {initials}
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-[11px] font-medium truncate ${isMe ? "text-[#4B6CF7]" : "text-[#444444]"}`}>
                          {member.display_name}
                        </span>
                        {isMe && <span className="text-[9px] text-[#4B6CF7]/60 shrink-0">나</span>}
                        {member.role === "admin" && (
                          <i className="ri-user-star-line text-[#D97706] text-[10px] shrink-0" title="관리자"></i>
                        )}
                      </div>
                    </div>
                    {/* Role badge */}
                    <div className="flex items-center gap-1 shrink-0">
                      <i className={`${roleIcon} text-[#BBBBBB] text-[10px]`}></i>
                      <span className="text-[#AAAAAA] text-[10px] whitespace-nowrap">{roleLabel}</span>
                    </div>
                  </div>
                );
              })}

              {/* 미배정 멤버 */}
              {noRole.length > 0 && (
                <>
                  <div className="text-[#DDDDDD] text-[9px] uppercase tracking-widest px-2.5 pt-2 pb-1">직무 미배정</div>
                  {noRole.map((member) => (
                    <div key={member.user_id} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg opacity-50">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-black/[0.06] text-[10px] font-bold text-[#AAAAAA] shrink-0">
                        {member.display_name.slice(0, 2)}
                      </div>
                      <span className="text-[#CCCCCC] text-[11px] flex-1 truncate">{member.display_name}</span>
                      <span className="text-[#DDDDDD] text-[10px]">미배정</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
